// Phase 36 Batch AM — Property Discovery page.
//
// Layout:
//   ┌──────────────────────────────────────────────────────────┐
//   │ Saved searches (rail) │  Selected search + listings grid │
//   └──────────────────────────────────────────────────────────┘
//
// Desktop (≥ md): rail is a fixed 280px sidebar.
// Mobile (< md):  rail collapses into a top dropdown; modal goes
//                full-screen courtesy of shadcn's responsive Dialog.
//
// Data flow:
//   1. on mount → GET /api/discovery/saved-searches  + /quota
//   2. on row click → setSelectedSearch(s); run() fires automatically
//   3. on "Run" → POST /saved-searches/{id}/run
//   4. on "Add as Deal" → POST /listings/{fp}/{id}/import-to-deal,
//      navigate to /deals/:id
//   5. quota refreshes after each `run`.
//
// All API calls go through the shared `api` axios instance (cookie auth +
// 401 interceptor inherited).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, RefreshCcw, Search, Trash2, Pencil, Inbox, AlertTriangle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import SavedSearchModal from "@/components/discovery/SavedSearchModal";
import ListingCard from "@/components/discovery/ListingCard";
import QuotaChip from "@/components/discovery/QuotaChip";
import {
  trackSearchCreated,
  trackSearchEdited,
  trackSearchDeleted,
  trackSearchRun,
  trackListingImported,
  trackQuotaWarning,
} from "@/lib/discoveryEvents";

const DISMISSED_KEY = "discovery_dismissed_listings";

function _filterCount(filters) {
  return Object.keys(filters || {}).length;
}

function _formatRelativeTime(iso) {
  if (!iso) return "Never run";
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch {
    return "Unknown";
  }
}

function _summary(filters) {
  const f = filters || {};
  const parts = [];
  if (f.city) parts.push(`${f.city}${f.state ? ", " + f.state.toUpperCase() : ""}`);
  else if (f.zip_code) parts.push(f.zip_code);
  if (f.property_type) parts.push(f.property_type);
  if (f.bedrooms_min) parts.push(`${f.bedrooms_min}+ bd`);
  if (f.max_price) parts.push(`≤ $${(f.max_price / 1000).toFixed(0)}k`);
  if (f.min_cap_rate_pct) parts.push(`${f.min_cap_rate_pct}%+ cap`);
  return parts.join(" · ");
}

function _readDismissed() {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function _writeDismissed(arr) {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(arr));
  } catch {
    /* sessionStorage disabled — fall back to runtime only */
  }
}

export default function Discovery() {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [results, setResults] = useState({ listings: [], source: null, warning: null });
  const [runLoading, setRunLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dismissed, setDismissed] = useState(_readDismissed());

  const selectedSearch = useMemo(
    () => searches.find((s) => s.id === selectedId) || null,
    [searches, selectedId],
  );

  // ─── Initial load ──────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [searchRes, quotaRes] = await Promise.all([
        api.get("/discovery/saved-searches"),
        api.get("/discovery/quota"),
      ]);
      setSearches(searchRes.data || []);
      setQuota(quotaRes.data || null);
      // Auto-select first search if nothing selected yet.
      if (!selectedId && (searchRes.data || []).length > 0) {
        setSelectedId(searchRes.data[0].id);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load discovery data");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Run search ────────────────────────────────────────────────────────
  const runSearch = useCallback(
    async (search) => {
      if (!search) return;
      setRunLoading(true);
      try {
        const res = await api.post(`/discovery/saved-searches/${search.id}/run`);
        setResults({
          listings: res.data?.listings || [],
          source: res.data?.source,
          warning: res.data?.warning || null,
          fingerprint: res.data?.fingerprint,
        });
        if (res.data?.quota) setQuota(res.data.quota);
        trackSearchRun({
          search_id: search.id,
          match_count: (res.data?.listings || []).length,
          source: res.data?.source,
        });
        if (res.data?.warning === "quota_low") {
          const remaining = res.data?.quota?.remaining_calls ?? quota?.remaining_calls ?? 0;
          trackQuotaWarning({ remaining_calls: remaining });
          if (remaining <= 0) {
            // Banner is rendered inline; no toast needed.
          } else {
            toast.warning(
              `Daily quota low (${remaining}/50 remaining) — showing cached results.`,
              { description: "New listings may not be fully fresh until next month's reset." },
            );
          }
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not run search");
      } finally {
        setRunLoading(false);
      }
    },
    [quota?.remaining_calls],
  );

  // Auto-run on selection change.
  useEffect(() => {
    if (selectedSearch) runSearch(selectedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ─── Saved-search CRUD handlers ────────────────────────────────────────
  const handleCreate = async (payload) => {
    const res = await api.post("/discovery/saved-searches", payload);
    setSearches((prev) => [res.data, ...prev]);
    setSelectedId(res.data.id);
    trackSearchCreated({
      filter_count: _filterCount(res.data.filters),
      location_type: res.data.filters?.zip_code ? "zip" : "city_state",
      has_email_digest: !!res.data.email_digest,
    });
    toast.success("Saved search created");
  };

  const handleUpdate = async (payload) => {
    if (!editing) return;
    const res = await api.patch(`/discovery/saved-searches/${editing.id}`, payload);
    setSearches((prev) => prev.map((s) => (s.id === editing.id ? res.data : s)));
    trackSearchEdited({ search_id: editing.id });
    toast.success("Saved search updated");
    if (selectedId === editing.id) {
      // Re-run to refresh results with new filters.
      runSearch(res.data);
    }
  };

  const handleDelete = async (search) => {
    if (!window.confirm(`Delete "${search.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/discovery/saved-searches/${search.id}`);
      setSearches((prev) => prev.filter((s) => s.id !== search.id));
      if (selectedId === search.id) {
        setSelectedId(null);
        setResults({ listings: [], source: null, warning: null });
      }
      trackSearchDeleted({ search_id: search.id });
      toast.success("Saved search deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete search");
    }
  };

  // ─── Import-to-deal ────────────────────────────────────────────────────
  const handleImport = async (listing) => {
    const fp = results.fingerprint;
    if (!fp || !listing?.rentcast_id) {
      toast.error("Missing listing reference — refresh and try again");
      return;
    }
    try {
      const res = await api.post(
        `/discovery/listings/${fp}/${listing.rentcast_id}/import-to-deal`,
      );
      trackListingImported({
        listing_id: listing.rentcast_id,
        deal_id_created: res.data?.deal_id,
      });
      toast.success("Deal created — opening detail view");
      navigate(`/deals/${res.data.deal_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import listing");
    }
  };

  // ─── Dismiss listing (session only) ────────────────────────────────────
  const handleDismiss = (listing) => {
    const next = Array.from(new Set([...dismissed, listing.rentcast_id]));
    setDismissed(next);
    _writeDismissed(next);
  };

  const visibleListings = useMemo(
    () => (results.listings || []).filter((l) => !dismissed.includes(l.rentcast_id)),
    [results.listings, dismissed],
  );

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <AppShell>
      <div data-testid="discovery-page" className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Discovery
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Save searches once. Get fresh listings + cap-rate signals every
              morning.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <QuotaChip quota={quota} />
            <Button
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
              size="sm"
              data-testid="discovery-new-search-btn"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <Plus className="mr-1 h-4 w-4" /> New search
            </Button>
          </div>
        </div>

        {quota && quota.remaining_calls === 0 && (
          <div
            data-testid="discovery-quota-exhausted-banner"
            className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-medium">Discovery is briefly paused this month.</p>
              <p className="text-xs text-rose-700">
                Our market data quota will refresh on{" "}
                {new Date(quota.resets_at).toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                })}
                . Existing cached results stay available.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div
            data-testid="discovery-load-error"
            className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          >
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={refresh}>
              Retry
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr] lg:grid-cols-[280px_1fr]">
          {/* ── Left rail ───────────────────────────────────────────── */}
          <aside
            data-testid="discovery-rail"
            className="space-y-2 md:max-h-[calc(100vh-220px)] md:overflow-y-auto"
          >
            {loading && searches.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500">
                Loading saved searches…
              </div>
            ) : searches.length === 0 ? (
              <div
                data-testid="discovery-empty-rail"
                className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center"
              >
                <Search className="mx-auto h-6 w-6 text-slate-400" />
                <p className="mt-2 text-sm font-medium text-slate-700">
                  No saved searches yet
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Create your first saved search to start discovering
                  properties.
                </p>
                <Button
                  onClick={() => {
                    setEditing(null);
                    setModalOpen(true);
                  }}
                  size="sm"
                  className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700"
                  data-testid="discovery-empty-cta"
                >
                  <Plus className="mr-1 h-4 w-4" /> Create your first
                </Button>
              </div>
            ) : (
              searches.map((s) => {
                const active = s.id === selectedId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    data-testid={`discovery-rail-item-${s.id}`}
                    className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      active
                        ? "border-indigo-200 bg-indigo-50"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <p className="truncate text-sm font-medium text-slate-900">
                      {s.name}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-slate-500">
                      {_summary(s.filters) || "No filters yet"}
                    </p>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                      <span>
                        {s.last_match_count != null
                          ? `${s.last_match_count} match${s.last_match_count === 1 ? "" : "es"}`
                          : "—"}
                      </span>
                      <span>{_formatRelativeTime(s.last_run_at)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </aside>

          {/* ── Right panel ─────────────────────────────────────────── */}
          <section
            data-testid="discovery-results-panel"
            className="space-y-4 min-w-0"
          >
            {selectedSearch ? (
              <>
                <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold text-slate-900">
                      {selectedSearch.name}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {_summary(selectedSearch.filters) || "No filters set"}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runSearch(selectedSearch)}
                      disabled={runLoading}
                      data-testid="discovery-run-btn"
                      className="gap-1.5"
                    >
                      <RefreshCcw
                        className={`h-3.5 w-3.5 ${runLoading ? "animate-spin" : ""}`}
                      />
                      Run
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(selectedSearch);
                        setModalOpen(true);
                      }}
                      data-testid="discovery-edit-btn"
                      className="gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(selectedSearch)}
                      data-testid="discovery-delete-btn"
                      className="gap-1.5 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {runLoading && visibleListings.length === 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                    Fetching listings…
                  </div>
                ) : visibleListings.length === 0 ? (
                  <div
                    data-testid="discovery-no-matches"
                    className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
                  >
                    <Inbox className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-3 text-sm font-medium text-slate-700">
                      {results.source === "empty" && results.warning === "quota_low"
                        ? "No cached results for this search"
                        : "No listings match this search right now"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Try widening price range, removing the cap-rate threshold,
                      or expanding the geography.
                    </p>
                  </div>
                ) : (
                  <div
                    data-testid="discovery-listings-grid"
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    {visibleListings.map((l) => (
                      <ListingCard
                        key={l.rentcast_id}
                        listing={l}
                        onImport={handleImport}
                        onDismiss={handleDismiss}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                <Search className="mx-auto h-8 w-8 text-slate-400" />
                <p className="mt-3 text-sm font-medium text-slate-700">
                  Pick a saved search to get started
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Or create a new one — discovery will fetch fresh listings
                  automatically.
                </p>
              </div>
            )}
          </section>
        </div>

        <SavedSearchModal
          open={modalOpen}
          onOpenChange={(o) => {
            setModalOpen(o);
            if (!o) setEditing(null);
          }}
          initial={editing}
          onSave={async (payload) => {
            if (editing) await handleUpdate(payload);
            else await handleCreate(payload);
          }}
        />
      </div>
    </AppShell>
  );
}
